import React, { useMemo, useState } from 'react';
import {
  BulkActionBar, ColumnPicker, ConfirmDialog, DataTable, DateRangePicker, DensityToggle,
  EmptyState, FilterBar, Pagination, SearchInput, SideDrawer, Toolbar,
  exportFilename, exportRows, formatThaiDate, useTableDensity, useToast,
  type ActiveFilter, type Column, type DateRange, type SortState,
} from '../components';
import { Icon } from '../components/base/Icon';
import {
  DOMAINS, MEMBER_TYPE_DISCOUNT, MEMBER_TYPE_LABEL, VERIFICATIONS,
  VERIFICATION_DAYS, VERIFICATION_WARN_DAYS,
  type MemberType, type Verification,
} from './memberTypeData';

/**
 * VerificationExpiryScreen — ติดตามการยืนยันสิทธิ์ที่ใกล้หมดอายุ (SRS 3.5)
 *
 * เพราะออกแบบให้ยืนยันสิทธิ์ใหม่ทุกปี จึงต้องมีที่ให้เห็นว่าใครใกล้หมดและใครหมดแล้ว
 * ถ้าไม่มีหน้านี้ สมาชิกจะเสียราคาพนักงานเงียบ ๆ แล้วมาร้องเรียนทีหลัง
 *
 * แบ่งเป็น 4 ช่วงที่ต้องทำงานต่างกัน:
 *   หมดอายุแล้ว        — เสียสิทธิ์ไปแล้ว ต้องเร่งให้ยืนยันใหม่
 *   เหลือไม่เกิน 7 วัน  — เตือนครั้งสุดท้าย
 *   เหลือ 8–30 วัน     — อยู่ในช่วงเตือนตามรอบปกติ
 *   ยังไม่ถึงช่วงเตือน  — ดูไว้เพื่อวางแผนกำลังคน
 *
 * การส่งเตือนซ้ำทำเป็นกลุ่มได้ (BulkActionBar) เพราะงานจริงคือส่งให้คนหลายร้อยคนพร้อมกัน
 * แต่ต้องยืนยันก่อนส่ง และบอกจำนวนคนที่จะได้รับ เพราะส่งแล้วเรียกคืนไม่ได้
 */

type ScreenState = 'ready' | 'loading' | 'error';
type Bucket = 'expired' | 'critical' | 'warning' | 'later' | 'all';

const BUCKET_LABEL: Record<Bucket, string> = {
  expired: 'หมดอายุแล้ว',
  critical: 'เหลือไม่เกิน 7 วัน',
  warning: 'เหลือ 8–30 วัน',
  later: 'ยังไม่ถึงช่วงเตือน',
  all: 'ทั้งหมด',
};

const bucketOf = (v: Verification): Exclude<Bucket, 'all'> =>
  v.daysLeft < 0 ? 'expired'
    : v.daysLeft <= 7 ? 'critical'
      : v.daysLeft <= VERIFICATION_WARN_DAYS ? 'warning' : 'later';

/** ป้ายวันหมดอายุ — มีไอคอนและข้อความกำกับ ไม่สื่อด้วยสีเดียว */
const ExpiryTag: React.FC<{ v: Verification }> = ({ v }) => {
  const b = bucketOf(v);
  const skin = {
    expired: { cls: 'border-[var(--error-border)] bg-[var(--error-bg)] text-[var(--error-text)]', icon: 'event_busy', text: `หมดอายุแล้ว ${-v.daysLeft} วัน` },
    critical: { cls: 'border-[var(--warning-border)] bg-[var(--warning-bg)] text-[var(--warning-text)]', icon: 'running_with_errors', text: `เหลือ ${v.daysLeft} วัน` },
    warning: { cls: 'border-[var(--warning-border)] bg-[var(--warning-bg)] text-[var(--warning-text)]', icon: 'schedule', text: `เหลือ ${v.daysLeft} วัน` },
    later: { cls: 'border-[var(--line-200)] bg-[var(--surface-100)] text-[var(--ink-700)]', icon: 'event_available', text: `เหลือ ${v.daysLeft} วัน` },
  }[b];
  return (
    <span className={`inline-flex items-center gap-1 rounded-[var(--radius-pill)] border px-2 py-[var(--space-05)] text-[length:var(--text-table-sm)] leading-[var(--leading-table)] ${skin.cls}`}>
      <Icon name={skin.icon} size={14} filled />
      {skin.text}
    </span>
  );
};

const MethodChip: React.FC<{ method: 'domain' | 'document'; domain?: string }> = ({ method, domain }) => (
  <span className="inline-flex items-center gap-1 rounded-[var(--radius-sm)] bg-[var(--surface-100)] px-2 py-[var(--space-05)] text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--ink-700)]">
    <Icon name={method === 'domain' ? 'alternate_email' : 'description'} size={14} />
    {method === 'domain' ? (domain ? `@${domain}` : 'โดเมนอีเมล') : 'เอกสารแนบ'}
  </span>
);

const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string }> = ({ label, children, hint }) => (
  <div className="flex flex-col gap-[var(--space-05)]">
    <span className="text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--ink-600)]">{label}</span>
    <span className="text-[length:var(--text-form-lg)] leading-[var(--leading-form)] font-[var(--weight-medium)] text-[var(--ink-900)]">{children}</span>
    {hint && <span className="text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--ink-600)]">{hint}</span>}
  </div>
);

const inputCls =
  'min-h-[var(--hit-control)] rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] px-[var(--space-2)] font-[family-name:var(--font-thai)] text-[length:var(--text-form)] text-[var(--ink-900)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2';

export interface VerificationExpiryScreenProps {
  initialState?: ScreenState;
}

export const VerificationExpiryScreen: React.FC<VerificationExpiryScreenProps> = ({ initialState = 'ready' }) => {
  const toast = useToast();
  const [screenState, setScreenState] = useState<ScreenState>(initialState);
  const [items, setItems] = useState<Verification[]>(VERIFICATIONS);
  const [bucket, setBucket] = useState<Bucket>('expired');
  const [density, setDensity] = useTableDensity();

  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | MemberType>('all');
  const [domainFilter, setDomainFilter] = useState<'all' | string>('all');
  const [renewalFilter, setRenewalFilter] = useState<'all' | 'started' | 'not-started'>('all');
  const [range, setRange] = useState<DateRange>({ from: null, to: null });
  const [sort, setSort] = useState<SortState | null>({ columnId: 'daysLeft', direction: 'asc' });
  const [visibleCols, setVisibleCols] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [selected, setSelected] = useState<string[]>([]);
  const [detail, setDetail] = useState<Verification | null>(null);
  const [confirmRemind, setConfirmRemind] = useState<string[] | null>(null);

  const domainName = (id: string | null) => DOMAINS.find((d) => d.id === id)?.domain;

  /* ---------------------------------------------------------- ตัวกรอง */

  const byBucket = useMemo(
    () => (bucket === 'all' ? items : items.filter((v) => bucketOf(v) === bucket)),
    [items, bucket],
  );

  const filtered = useMemo(() => byBucket.filter((v) => {
    if (typeFilter !== 'all' && v.memberType !== typeFilter) return false;
    if (domainFilter !== 'all' && v.domainId !== domainFilter) return false;
    if (renewalFilter === 'started' && !v.renewalStarted) return false;
    if (renewalFilter === 'not-started' && v.renewalStarted) return false;
    if (range.from && v.expiresAt < range.from) return false;
    if (range.to && v.expiresAt > range.to) return false;
    if (query) {
      const q = query.trim().toLowerCase();
      if (!v.member.name.includes(q) && !v.member.memberCode.toLowerCase().includes(q) && !v.member.email.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [byBucket, typeFilter, domainFilter, renewalFilter, range, query]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const dir = sort.direction === 'asc' ? 1 : -1;
    const key = (v: Verification): string | number =>
      sort.columnId === 'daysLeft' ? v.daysLeft
        : sort.columnId === 'yearSpend' ? v.yearSpend
          : sort.columnId === 'remindersSent' ? v.remindersSent
            : sort.columnId === 'memberType' ? MEMBER_TYPE_LABEL[v.memberType]
              : sort.columnId === 'expiresAt' ? v.expiresAt
                : v.member.name;
    return [...filtered].sort((a, b) => {
      const ka = key(a); const kb = key(b);
      return (typeof ka === 'number' && typeof kb === 'number' ? ka - kb : String(ka).localeCompare(String(kb), 'th')) * dir;
    });
  }, [filtered, sort]);

  const rows = sorted.slice((page - 1) * pageSize, page * pageSize);

  const counts = useMemo(() => ({
    expired: items.filter((v) => bucketOf(v) === 'expired').length,
    critical: items.filter((v) => bucketOf(v) === 'critical').length,
    warning: items.filter((v) => bucketOf(v) === 'warning').length,
    later: items.filter((v) => bucketOf(v) === 'later').length,
    all: items.length,
  }), [items]);

  /** ยอดซื้อรวมของกลุ่มที่หมดอายุแล้ว — ใช้บอกว่าปัญหานี้ใหญ่แค่ไหนเป็นตัวเงิน */
  const expiredSpend = useMemo(
    () => items.filter((v) => bucketOf(v) === 'expired').reduce((s, v) => s + v.yearSpend, 0),
    [items],
  );

  /* --------------------------------------------------------- คอลัมน์ */

  const columns: Array<Column<Verification>> = useMemo(() => [
    {
      id: 'member', header: 'สมาชิก', width: 188, pinned: true, required: true, sortable: true,
      value: (v) => v.member.name,
      cell: (v) => (
        <span className="flex flex-col leading-[1.3]">
          <span className="bo-truncate">{v.member.name}</span>
          <span className="bo-num bo-truncate text-[length:var(--text-table-sm)] text-[var(--ink-700)]" style={{ textAlign: 'left' }}>{v.member.memberCode}</span>
        </span>
      ),
    },
    {
      id: 'memberType', header: 'ประเภทสมาชิก', width: 200, sortable: true,
      value: (v) => MEMBER_TYPE_LABEL[v.memberType],
      cell: (v) => (
        <span className="inline-flex items-center gap-1 rounded-[var(--radius-sm)] bg-[var(--blue-050)] px-2 py-[var(--space-05)] text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--blue-900)]">
          <Icon name="badge" size={14} />
          {MEMBER_TYPE_LABEL[v.memberType]}
          {MEMBER_TYPE_DISCOUNT[v.memberType] !== undefined && (
            <span className="bo-num text-[var(--ink-700)]">{MEMBER_TYPE_DISCOUNT[v.memberType]}%</span>
          )}
        </span>
      ),
    },
    { id: 'method', header: 'ยืนยันด้วย', width: 176, value: (v) => (v.method === 'domain' ? domainName(v.domainId) ?? 'โดเมน' : 'เอกสาร'), cell: (v) => <MethodChip method={v.method} domain={domainName(v.domainId)} /> },
    {
      id: 'daysLeft', header: 'สถานะการยืนยัน', width: 168, sortable: true, value: (v) => v.daysLeft,
      cell: (v) => <ExpiryTag v={v} />,
    },
    { id: 'expiresAt', header: 'วันหมดอายุ', width: 124, sortable: true, value: (v) => v.expiresAt, cell: (v) => <span className="bo-num" style={{ textAlign: 'left' }}>{formatThaiDate(v.expiresAt)}</span> },
    { id: 'verifiedAt', header: 'ยืนยันครั้งล่าสุด', width: 130, value: (v) => v.verifiedAt, cell: (v) => <span className="bo-num" style={{ textAlign: 'left' }}>{formatThaiDate(v.verifiedAt)}</span> },
    {
      id: 'remindersSent', header: 'เตือนไปแล้ว', width: 152, numeric: true, sortable: true,
      value: (v) => v.remindersSent,
      cell: (v) => (v.remindersSent === 0
        ? <span className="text-[var(--ink-400)]">ยังไม่เตือน</span>
        : (
          <span className="flex flex-col items-end leading-[1.3]">
            <span className="bo-num">{v.remindersSent} ครั้ง</span>
            {v.lastReminderAt && <span className="bo-num text-[length:var(--text-table-sm)] text-[var(--ink-700)]">ล่าสุด {v.lastReminderAt}</span>}
          </span>
        )),
    },
    {
      id: 'renewalStarted', header: 'เริ่มยืนยันใหม่แล้ว', width: 156,
      value: (v) => (v.renewalStarted ? 'เริ่มแล้ว' : 'ยังไม่เริ่ม'),
      cell: (v) => (v.renewalStarted
        ? <span className="inline-flex items-center gap-1 text-[var(--success-text)]"><Icon name="how_to_reg" size={14} filled />เริ่มแล้ว</span>
        : <span className="inline-flex items-center gap-1 text-[var(--ink-700)]"><Icon name="pending" size={14} />ยังไม่เริ่ม</span>),
    },
    {
      id: 'yearSpend', header: 'ยอดซื้อในรอบปี (บาท)', width: 168, numeric: true, sortable: true,
      value: (v) => v.yearSpend,
      cell: (v) => <span className="bo-num">{v.yearSpend.toLocaleString('th-TH')}</span>,
    },
    { id: 'email', header: 'อีเมล', width: 260, value: (v) => v.member.email, cell: (v) => <span className="bo-num bo-truncate" style={{ textAlign: 'left' }}>{v.member.email}</span> },
    { id: 'phone', header: 'เบอร์โทร', width: 124, value: (v) => v.member.phone, cell: (v) => <span className="bo-num" style={{ textAlign: 'left' }}>{v.member.phone}</span> },
  ], []);

  const visible = visibleCols.length ? visibleCols : columns.map((c) => c.id);

  const activeFilters: ActiveFilter[] = useMemo(() => {
    const out: ActiveFilter[] = [];
    if (typeFilter !== 'all') out.push({ id: 'type', field: 'ประเภทสมาชิก', display: MEMBER_TYPE_LABEL[typeFilter] });
    if (domainFilter !== 'all') out.push({ id: 'domain', field: 'โดเมน', display: '@' + (domainName(domainFilter) ?? domainFilter) });
    if (renewalFilter !== 'all') out.push({ id: 'renewal', field: 'การยืนยันใหม่', display: renewalFilter === 'started' ? 'เริ่มแล้ว' : 'ยังไม่เริ่ม' });
    if (range.from || range.to) out.push({ id: 'range', field: 'วันหมดอายุ', display: `${formatThaiDate(range.from)} – ${formatThaiDate(range.to)}` });
    return out;
  }, [typeFilter, domainFilter, renewalFilter, range]);

  const clearAll = () => {
    setTypeFilter('all'); setDomainFilter('all'); setRenewalFilter('all');
    setRange({ from: null, to: null }); setQuery(''); setPage(1);
  };

  const doExport = (format: 'csv' | 'excel') => {
    const n = exportRows({ columns, rows: sorted, columnIds: visible, filename: exportFilename(`การยืนยันสิทธิ์-${BUCKET_LABEL[bucket]}`), format });
    toast.show({ tone: 'success', message: `ส่งออก ${n.toLocaleString('th-TH')} รายการแล้ว — บันทึกใน Audit Log` });
  };

  const sendReminders = (ids: string[]) => {
    const before = items;
    setItems((prev) => prev.map((v) => (ids.includes(v.id)
      ? { ...v, remindersSent: v.remindersSent + 1, lastReminderAt: '2569-08-21' }
      : v)));
    setSelected([]);
    setConfirmRemind(null);
    toast.show({
      tone: 'success',
      message: `ส่งการแจ้งเตือนให้ ${ids.length.toLocaleString('th-TH')} คนแล้ว — ทาง LINE และอีเมล`,
      undo: { label: 'ย้อนการบันทึก', onUndo: () => setItems(before), seconds: 8 },
    });
  };

  const emptyState = activeFilters.length || query
    ? <EmptyState kind="no-results" title="ไม่พบรายการที่ตรงกับตัวกรอง" action={{ label: 'ล้างตัวกรองทั้งหมด', onClick: clearAll }} />
    : bucket === 'expired'
      ? <EmptyState kind="queue-clear" title="ไม่มีสิทธิ์ที่หมดอายุค้างอยู่" description="สมาชิกทุกคนที่ถึงรอบยืนยันได้ยืนยันใหม่เรียบร้อยแล้ว" />
      : <EmptyState kind="no-data" title="ไม่มีรายการในช่วงนี้" description="ยังไม่มีสมาชิกที่การยืนยันตกอยู่ในช่วงเวลานี้" />;

  return (
    <div className="flex h-screen min-w-[var(--bo-min-width)] flex-col bg-[var(--surface-0)] font-[family-name:var(--font-thai)]">
      <Toolbar
        title="การยืนยันสิทธิ์ที่ใกล้หมดอายุ"
        count={counts.expired + counts.critical}
        center={<SearchInput value={query} onChange={(v) => { setQuery(v); setPage(1); }} focusOnSlash placeholder="ค้นหาชื่อ รหัสสมาชิก หรืออีเมล" width={300} />}
        end={
          <>
            <DensityToggle value={density} onChange={setDensity} />
            <ColumnPicker columns={columns} persistKey="verification-expiry" value={visible} onChange={setVisibleCols} />
            <div className="flex items-center gap-[var(--space-05)] rounded-[var(--radius-sm)] border border-[var(--line-300)] p-[var(--space-05)]">
              <button type="button" onClick={() => doExport('excel')} className="inline-flex min-h-[var(--hit-icon-sm)] items-center gap-1 rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]">
                <Icon name="download" size={16} />Excel
              </button>
              <button type="button" onClick={() => doExport('csv')} className="inline-flex min-h-[var(--hit-icon-sm)] items-center rounded-[var(--radius-xs)] px-[var(--space-15)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]">CSV</button>
            </div>
          </>
        }
      />

      {/* ค่าอายุการยืนยันยังเป็นค่าสมมติ — ต้องเห็นทุกครั้ง (PROGRESS-REPORT 8.4) */}
      <div className="flex items-start gap-[var(--space-2)] border-b border-[var(--line-200)] bg-[var(--warning-bg)] px-[var(--space-4)] py-[var(--space-2)]">
        <Icon name="warning" size={19} filled className="text-[var(--warning-text)]" />
        <p className="m-0 max-w-[104ch] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-900)]">
          <strong className="text-[var(--warning-text)]">อายุการยืนยัน {VERIFICATION_DAYS} วัน และการเตือนล่วงหน้า {VERIFICATION_WARN_DAYS} วัน ยังเป็นค่าสมมติ</strong> —
          ต้องยืนยันจากลูกค้าว่ารอบจริงเป็นเท่าไร ต้องเตือนล่วงหน้ากี่ครั้ง และถ้าไม่ยืนยันใหม่ทันจะลดสิทธิ์ทันทีหรือมีช่วงผ่อนผัน
          ค่าเหล่านี้ตั้งได้ที่หน้าตั้งค่าระบบเมื่อได้คำตอบ
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-[var(--space-4)] border-b border-[var(--line-200)] bg-[var(--blue-050)] px-[var(--space-4)] py-[var(--space-2)]">
        <span className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          <Icon name="event_busy" size={18} className="text-[var(--error-text)]" />
          หมดอายุแล้ว
          <strong className="bo-num text-[length:var(--text-section)] font-[var(--weight-bold)] text-[var(--error-text)]" style={{ textAlign: 'left' }}>{counts.expired.toLocaleString('th-TH')}</strong>
          คน
        </span>
        <span className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          <Icon name="payments" size={18} className="text-[var(--blue-700)]" />
          ยอดซื้อรอบปีของกลุ่มที่หมดอายุ
          <strong className="bo-num text-[length:var(--text-section)] font-[var(--weight-bold)] text-[var(--ink-900)]" style={{ textAlign: 'left' }}>{expiredSpend.toLocaleString('th-TH')}</strong>
          บาท
        </span>
        <span className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          <Icon name="how_to_reg" size={18} className="text-[var(--success-text)]" />
          เริ่มยืนยันใหม่แล้ว
          <strong className="bo-num text-[length:var(--text-section)] font-[var(--weight-bold)] text-[var(--ink-900)]" style={{ textAlign: 'left' }}>
            {items.filter((v) => v.renewalStarted).length.toLocaleString('th-TH')}
          </strong>
          คน
        </span>
      </div>

      <div role="tablist" aria-label="ช่วงเวลาที่หมดอายุ" className="flex items-end gap-[var(--space-1)] border-b border-[var(--line-200)] px-[var(--space-4)]">
        {(['expired', 'critical', 'warning', 'later', 'all'] as Bucket[]).map((b) => {
          const on = bucket === b;
          const n = counts[b];
          const urgent = b === 'expired' && n > 0;
          return (
            <button
              key={b}
              role="tab"
              type="button"
              aria-selected={on}
              onClick={() => { setBucket(b); setPage(1); setSelected([]); }}
              className={`inline-flex min-h-[var(--hit-primary)] items-center gap-[var(--space-15)] border-b-2 px-[var(--space-3)] text-[length:var(--text-form-lg)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-1 ${
                on ? 'border-[var(--blue-700)] font-[var(--weight-semibold)] text-[var(--blue-700)]' : 'border-transparent text-[var(--ink-700)] hover:border-[var(--line-300)] hover:text-[var(--ink-900)]'
              }`}
            >
              {urgent && <Icon name="priority_high" size={16} className="text-[var(--error-text)]" />}
              {BUCKET_LABEL[b]}
              <span className={`bo-num rounded-[var(--radius-pill)] px-[var(--space-15)] text-[length:var(--text-table-sm)] font-[var(--weight-semibold)] ${
                on ? 'bg-[var(--blue-700)] text-white' : urgent ? 'bg-[var(--error-bg)] text-[var(--error-text)]' : 'bg-[var(--surface-100)] text-[var(--ink-700)]'
              }`}>
                {n.toLocaleString('th-TH')}
              </span>
            </button>
          );
        })}
      </div>

      <FilterBar
        active={activeFilters}
        onRemove={(id) => {
          if (id === 'type') setTypeFilter('all');
          if (id === 'domain') setDomainFilter('all');
          if (id === 'renewal') setRenewalFilter('all');
          if (id === 'range') setRange({ from: null, to: null });
          setPage(1);
        }}
        onClearAll={clearAll}
        currentFilters={{ typeFilter, domainFilter, renewalFilter, range }}
        persistKey="verification-expiry"
        resultCount={sorted.length}
      >
        <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          ประเภทสมาชิก
          <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value as typeof typeFilter); setPage(1); }} className={inputCls}>
            <option value="all">ทั้งหมด</option>
            {(['ptt-employee', 'ptt-group', 'ptt-retired'] as MemberType[]).map((t) => (
              <option key={t} value={t}>{MEMBER_TYPE_LABEL[t]}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          โดเมน
          <select value={domainFilter} onChange={(e) => { setDomainFilter(e.target.value); setPage(1); }} className={inputCls}>
            <option value="all">ทุกโดเมน</option>
            {DOMAINS.map((d) => <option key={d.id} value={d.id}>@{d.domain}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)]">
          การยืนยันใหม่
          <select value={renewalFilter} onChange={(e) => { setRenewalFilter(e.target.value as typeof renewalFilter); setPage(1); }} className={inputCls}>
            <option value="all">ทั้งหมด</option>
            <option value="not-started">ยังไม่เริ่ม</option>
            <option value="started">เริ่มแล้ว</option>
          </select>
        </label>
        <DateRangePicker value={range} onChange={(r) => { setRange(r); setPage(1); }} label="วันหมดอายุ" />
      </FilterBar>

      <div className="relative flex min-h-0 flex-1 flex-col">
        <DataTable<Verification>
          columns={columns.filter((c) => visible.includes(c.id))}
          rows={rows}
          rowKey={(v) => v.id}
          caption={`การยืนยันสิทธิ์ — ${BUCKET_LABEL[bucket]} ${sorted.length} รายการ`}
          state={screenState}
          rowCountHint={12}
          errorMessage="โหลดข้อมูลการยืนยันสิทธิ์ไม่สำเร็จ — เชื่อมต่อเซิร์ฟเวอร์ไม่ได้"
          onRetry={() => setScreenState('ready')}
          emptyState={emptyState}
          selectable
          selectedKeys={selected}
          onSelectionChange={setSelected}
          sort={sort}
          onSortChange={setSort}
          onOpenRow={setDetail}
          density={density}
          persistKey="verification-expiry"
          isFlagged={(v) => bucketOf(v) === 'expired'}
          className="min-h-0 flex-1 rounded-none border-0"
        />

        <BulkActionBar
          selectedKeys={selected}
          totalMatching={sorted.length}
          onSelectAllMatching={() => setSelected(sorted.map((v) => v.id))}
          onClear={() => setSelected([])}
          actions={[
            {
              id: 'remind',
              label: 'ส่งการแจ้งเตือนซ้ำ',
              icon: 'notifications_active',
              tone: 'primary',
              consequence: 'ส่งทาง LINE และอีเมล — ส่งแล้วเรียกคืนไม่ได้',
              onRun: (keys) => setConfirmRemind(keys),
            },
            {
              id: 'export-selected',
              label: 'ส่งออกที่เลือก',
              icon: 'download',
              onRun: (keys) => {
                const picked = sorted.filter((v) => keys.includes(v.id));
                const n = exportRows({ columns, rows: picked, columnIds: visible, filename: exportFilename('การยืนยันสิทธิ์-ที่เลือก'), format: 'excel' });
                toast.show({ tone: 'success', message: `ส่งออก ${n.toLocaleString('th-TH')} รายการที่เลือกแล้ว` });
              },
            },
          ]}
        />
      </div>

      <Pagination page={page} pageSize={pageSize} totalItems={sorted.length} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} pageSizeOptions={[25, 50, 100, 200]} />

      {/* ---------------------------------------------- แผงรายละเอียด */}
      <SideDrawer
        open={Boolean(detail)}
        onClose={() => setDetail(null)}
        title={detail?.member.name ?? ''}
        subtitle={detail?.member.memberCode}
        footer={detail && (
          <>
            <button
              type="button"
              onClick={() => { setConfirmRemind([detail.id]); setDetail(null); }}
              className="inline-flex min-h-[var(--hit-primary)] items-center gap-[var(--space-15)] rounded-[var(--radius-sm)] bg-[var(--blue-700)] px-[var(--space-4)] text-[length:var(--text-form)] font-[var(--weight-semibold)] text-white hover:bg-[var(--blue-800)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
            >
              <Icon name="notifications_active" size={18} />
              ส่งการแจ้งเตือนซ้ำ
            </button>
            <a
              href={`/customers/${detail.member.id}`}
              className="inline-flex min-h-[var(--hit-primary)] items-center rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-4)] text-[length:var(--text-form)] font-[var(--weight-medium)] no-underline"
            >
              เปิด Customer 360
            </a>
          </>
        )}
      >
        {detail && (
          <div className="flex flex-col gap-[var(--space-4)]">
            <div className="flex flex-wrap items-center gap-[var(--space-2)]">
              <ExpiryTag v={detail} />
              <MethodChip method={detail.method} domain={domainName(detail.domainId)} />
            </div>

            <div className="grid grid-cols-2 gap-[var(--space-3)]">
              <Field label="ประเภทสมาชิก" hint={MEMBER_TYPE_DISCOUNT[detail.memberType] !== undefined ? `ส่วนลด ${MEMBER_TYPE_DISCOUNT[detail.memberType]}%` : 'อัตราส่วนลดยังไม่กำหนด'}>
                {MEMBER_TYPE_LABEL[detail.memberType]}
              </Field>
              <Field label="วันหมดอายุ" hint={detail.daysLeft < 0 ? `หมดอายุแล้ว ${-detail.daysLeft} วัน` : `เหลืออีก ${detail.daysLeft} วัน`}>
                <span className="bo-num" style={{ textAlign: 'left' }}>{formatThaiDate(detail.expiresAt)}</span>
              </Field>
              <Field label="ยืนยันครั้งล่าสุด">
                <span className="bo-num" style={{ textAlign: 'left' }}>{formatThaiDate(detail.verifiedAt)}</span>
              </Field>
              <Field label="ยอดซื้อในรอบปี" hint="ยอดที่ได้รับส่วนลดจากสิทธิ์นี้">
                <span className="bo-num" style={{ textAlign: 'left' }}>{detail.yearSpend.toLocaleString('th-TH')} บาท</span>
              </Field>
              <Field label="อีเมล">
                <span className="bo-num text-[length:var(--text-table)]" style={{ textAlign: 'left' }}>{detail.member.email}</span>
              </Field>
              <Field label="เบอร์โทร">
                <span className="bo-num" style={{ textAlign: 'left' }}>{detail.member.phone}</span>
              </Field>
            </div>

            <section className="flex flex-col gap-[var(--space-1)] rounded-[var(--radius-md)] border border-[var(--line-200)] bg-[var(--surface-100)] p-[var(--space-3)]">
              <h3 className="m-0 text-[length:var(--text-form)] font-[var(--weight-semibold)] text-[var(--ink-700)]">การแจ้งเตือนที่ส่งไปแล้ว</h3>
              <p className="m-0 text-[length:var(--text-form-lg)] leading-[var(--leading-thai)] text-[var(--ink-900)]">
                {detail.remindersSent === 0
                  ? 'ยังไม่ได้ส่งการแจ้งเตือน'
                  : `ส่งแล้ว ${detail.remindersSent} ครั้ง — ล่าสุด ${detail.lastReminderAt}`}
              </p>
              <p className="m-0 text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                {detail.renewalStarted
                  ? 'สมาชิกเริ่มกระบวนการยืนยันใหม่แล้ว รออนุมัติหรือรอยืนยันอีเมล'
                  : 'สมาชิกยังไม่เริ่มยืนยันใหม่'}
              </p>
            </section>

            {detail.method === 'domain' && !DOMAINS.find((d) => d.id === detail.domainId)?.enabled && (
              <div role="alert" className="flex items-start gap-[var(--space-2)] rounded-[var(--radius-md)] border border-[var(--warning-border)] bg-[var(--warning-bg)] p-[var(--space-3)] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-900)]">
                <Icon name="dns" size={18} filled className="text-[var(--warning-text)]" />
                <span>
                  โดเมนที่ใช้ยืนยันครั้งก่อนถูกปิดใช้งานแล้ว — สมาชิกคนนี้ยืนยันด้วยโดเมนเดิมไม่ได้
                  ต้องยืนยันด้วยเอกสารหรือโดเมนใหม่ของบริษัท
                </span>
              </div>
            )}
          </div>
        )}
      </SideDrawer>

      {/* -------------------------------------- ยืนยันก่อนส่งเตือนซ้ำ */}
      <ConfirmDialog
        open={Boolean(confirmRemind)}
        title="ส่งการแจ้งเตือนซ้ำ"
        description={confirmRemind ? (
          <>
            <p className="m-0 mb-[var(--space-2)]">
              ส่งการแจ้งเตือนให้สมาชิก <strong className="bo-num">{confirmRemind.length.toLocaleString('th-TH')}</strong> คน
              ทาง LINE และอีเมล เพื่อให้ยืนยันสิทธิ์ใหม่
            </p>
            <p className="m-0 text-[length:var(--text-form)] text-[var(--ink-600)]">
              ส่งแล้วเรียกคืนไม่ได้ — สมาชิกที่ได้รับเตือนไปแล้ว 3 ครั้งจะได้รับซ้ำเป็นครั้งที่ 4
            </p>
          </>
        ) : ''}
        confirmLabel="ส่งการแจ้งเตือน"
        onConfirm={() => confirmRemind && sendReminders(confirmRemind)}
        onCancel={() => setConfirmRemind(null)}
      />
    </div>
  );
};

export default VerificationExpiryScreen;

/* ============================================================================
   รองรับ:
   SRS 3.5    เงื่อนไขพิเศษกลุ่ม PTT Group — การยืนยันสิทธิ์มีอายุและต้องยืนยันใหม่ตามรอบ
   FR-MTYPE-06 เมื่อสิทธิ์หมดอายุ ประเภทสมาชิกและโครงสร้างราคาเปลี่ยนกลับ
              หน้านี้จึงแสดงยอดซื้อในรอบปีเพื่อให้เห็นผลกระทบเป็นตัวเงิน
   FR-ADM-08  รายการที่หมดอายุแล้วและเริ่มยืนยันใหม่จะไหลกลับเข้าคิวอนุมัติประเภทสมาชิก
   FR-ADM-37  การส่งการแจ้งเตือนซ้ำและการส่งออกเข้า Audit Log
   FR-ADM-39  การส่งออกถูกบันทึกและแจ้งผู้ใช้

   ประเด็นค้าง (PROGRESS-REPORT 8.4 — ต้องได้คำตอบก่อนใช้งานจริง):
   — รอบการยืนยันจริงเป็นเท่าไร (ตอนนี้สมมติ 365 วัน)
   — ต้องเตือนล่วงหน้ากี่วันและกี่ครั้ง (ตอนนี้สมมติ 30 วัน)
   — ถ้าไม่ยืนยันใหม่ทัน จะลดสิทธิ์ทันทีหรือมีช่วงผ่อนผัน และผ่อนผันกี่วัน
   — คำสั่งซื้อที่ค้างอยู่ตอนสิทธิ์หมดอายุ จะคิดราคาเดิมหรือราคาใหม่
   ============================================================================ */
